/**
 * What each toolbar button does to the text in the box.
 *
 * Kept apart from the component because this is the part that can be wrong: where the markers go,
 * what happens on a second press, and which lines a list prefix lands on. None of it needs a DOM,
 * so all of it is tested directly.
 *
 * The editor stores markdown, not HTML. The buttons exist so an organiser who has never heard of
 * markdown never has to type a marker - not so the syntax can be hidden from anyone who has.
 */
export type MarkdownAction = 'bold' | 'italic' | 'bulletList' | 'numberList' | 'quote' | 'link';

export interface MarkdownEdit {
    value: string;
    selectionStart: number;
    selectionEnd: number;
}

const WRAPPERS: Partial<Record<MarkdownAction, string>> = {
    bold: '**',
    italic: '*'
};

/** Prefix applied per line. numberList is handled separately, since its prefix counts. */
const LINE_PREFIXES: Partial<Record<MarkdownAction, string>> = {
    bulletList: '- ',
    quote: '> '
};

export function applyMarkdown(value: string, selectionStart: number, selectionEnd: number, action: MarkdownAction): MarkdownEdit {
    const text = value ?? '';
    const start = Math.max(0, Math.min(selectionStart ?? 0, text.length));
    const end = Math.max(start, Math.min(selectionEnd ?? start, text.length));

    if (action === 'link') {
        return applyLink(text, start, end);
    }
    if (action === 'bulletList' || action === 'numberList' || action === 'quote') {
        return applyLinePrefix(text, start, end, action);
    }
    return applyWrapper(text, start, end, WRAPPERS[action]!);
}

function applyWrapper(text: string, start: number, end: number, marker: string): MarkdownEdit {
    const selected = text.slice(start, end);

    if (!selected) {
        // Markers with the caret between them, so typing carries straight on inside them.
        return {
            value: text.slice(0, start) + marker + marker + text.slice(start),
            selectionStart: start + marker.length,
            selectionEnd: start + marker.length
        };
    }

    if (isWrappedWith(selected, marker)) {
        const inner = selected.slice(marker.length, selected.length - marker.length);
        return { value: text.slice(0, start) + inner + text.slice(end), selectionStart: start, selectionEnd: start + inner.length };
    }

    // The markers can also sit just OUTSIDE the selection, which is the usual case: wrapping
    // leaves the inner text selected so a second press can undo it, and that selection excludes
    // the markers it just added.
    if (isSurroundedBy(text, start, end, marker)) {
        return {
            value: text.slice(0, start - marker.length) + selected + text.slice(end + marker.length),
            selectionStart: start - marker.length,
            selectionEnd: end - marker.length
        };
    }

    return {
        value: text.slice(0, start) + marker + selected + marker + text.slice(end),
        selectionStart: start + marker.length,
        selectionEnd: end + marker.length
    };
}

/**
 * Whether the text is already wrapped in exactly this marker.
 *
 * The length test is what keeps italic off bold: `**x**` both starts and ends with `*`, so a naive
 * check would unwrap one from each side and silently turn bold into italic.
 */
function isWrappedWith(selected: string, marker: string): boolean {
    if (selected.length < marker.length * 2 || !selected.startsWith(marker) || !selected.endsWith(marker)) {
        return false;
    }
    const inner = selected.slice(marker.length, selected.length - marker.length);
    return !inner.startsWith(marker.charAt(0)) && !inner.endsWith(marker.charAt(0));
}

/**
 * Whether this exact marker sits immediately either side of the selection.
 *
 * The outward look-ahead is the bold/italic guard again from the other direction: the `parking`
 * inside `**parking**` is surrounded by `*`, and without checking one character further out the
 * italic button would strip bold's inner asterisks and leave `*parking*`.
 */
function isSurroundedBy(text: string, start: number, end: number, marker: string): boolean {
    if (start < marker.length || end + marker.length > text.length) {
        return false;
    }
    if (text.slice(start - marker.length, start) !== marker || text.slice(end, end + marker.length) !== marker) {
        return false;
    }
    const outerBefore = text.charAt(start - marker.length - 1);
    const outerAfter = text.charAt(end + marker.length);
    return outerBefore !== marker.charAt(0) && outerAfter !== marker.charAt(0);
}

function applyLinePrefix(text: string, start: number, end: number, action: MarkdownAction): MarkdownEdit {
    const lineStart = text.lastIndexOf('\n', Math.max(0, start - 1)) + 1;
    const lineEndIndex = text.indexOf('\n', end);
    const lineEnd = lineEndIndex === -1 ? text.length : lineEndIndex;

    const block = text.slice(lineStart, lineEnd);
    const lines = block.split('\n');
    // Blank lines are left alone throughout: a bullet with nothing after it is not a list item,
    // and a blank line is how an organiser separates paragraphs.
    const content = lines.filter((line) => line.trim().length);
    const existing = (line: string) => (action === 'numberList' ? /^\d+\.\s/.exec(line)?.[0] : LINE_PREFIXES[action]);

    const allPrefixed = content.length > 0 && content.every((line) => !!existing(line) && line.startsWith(existing(line)!));

    let counter = 0;
    const next = lines
        .map((line) => {
            if (!line.trim().length) {
                return line;
            }
            if (allPrefixed) {
                return line.slice(existing(line)!.length);
            }
            counter++;
            return (action === 'numberList' ? `${counter}. ` : LINE_PREFIXES[action]!) + line;
        })
        .join('\n');

    return {
        value: text.slice(0, lineStart) + next + text.slice(lineEnd),
        selectionStart: lineStart,
        selectionEnd: lineStart + next.length
    };
}

function applyLink(text: string, start: number, end: number): MarkdownEdit {
    const label = text.slice(start, end) || 'text';
    const inserted = `[${label}](url)`;
    // The url is selected rather than the label: the label is usually already right, and the url
    // is the part that always has to be typed.
    const urlStart = start + inserted.length - 4;

    return {
        value: text.slice(0, start) + inserted + text.slice(end),
        selectionStart: urlStart,
        selectionEnd: urlStart + 3
    };
}
